import React from 'react';
import { Icon } from '@iconify/react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'icon';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const sizeMap: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '6px 14px', fontSize: '13px', borderRadius: 'var(--radius-sm)' },
  md: { padding: '10px 20px', fontSize: '14px', borderRadius: 'var(--radius-md)' },
  lg: { padding: '12px 28px', fontSize: '15px', borderRadius: 'var(--radius-md)' },
};

const variantMap: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: 'linear-gradient(135deg, #0D9488, #0F766E)',
    color: '#FFFFFF',
    border: 'none',
    boxShadow: '0 4px 12px rgba(13, 148, 136, 0.30)',
  },
  secondary: {
    background: 'var(--glass-bg-light)',
    backdropFilter: 'blur(12px)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--glass-border)',
  },
  danger: {
    background: 'linear-gradient(135deg, #EF4444, #DC2626)',
    color: '#FFFFFF',
    border: 'none',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid transparent',
  },
  icon: {
    width: '36px',
    height: '36px',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--glass-bg-light)',
    backdropFilter: 'blur(12px)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-muted)',
  },
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  children,
  style,
  disabled,
  ...rest
}) => {
  const baseStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all var(--transition-base)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    opacity: disabled ? 0.5 : 1,
    width: fullWidth ? '100%' : undefined,
    ...(variant !== 'icon' ? sizeMap[size] : {}),
    ...variantMap[variant],
    ...style,
  };

  return (
    <button style={baseStyle} disabled={disabled || loading} {...rest}>
      {loading && (
        <span style={{
          width: '16px',
          height: '16px',
          border: '2px solid rgba(255,255,255,0.3)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          flexShrink: 0,
        }} />
      )}
      {!loading && icon && iconPosition === 'left' && <Icon icon={icon} width={18} />}
      {children}
      {!loading && icon && iconPosition === 'right' && <Icon icon={icon} width={18} />}
    </button>
  );
};

export default Button;
